import CompanyBranding from '@src/components/CompanyBranding';
import { openConsentPreferences } from '@src/components/consent';
import { POLICY_VERSION } from '@src/corelib/consent';

const NECESSARY_COOKIES = [
  {
    name: 'sessionid',
    provider: 'SecureOneLabs (first party)',
    purpose: 'Maintains your session while using the site.',
    duration: 'Session',
  },
  {
    name: 'csrftoken',
    provider: 'SecureOneLabs (first party)',
    purpose: 'Protects form submissions against cross-site request forgery.',
    duration: '1 year',
  },
  {
    name: 'refresh_token',
    provider: 'SecureOneLabs (first party)',
    purpose: 'Keeps you signed in. httpOnly and never readable by JavaScript.',
    duration: 'Session',
  },
  {
    name: 'sol_consent',
    provider: 'SecureOneLabs (first party)',
    purpose: 'Remembers the cookie choice you made on this page. Storing a refusal is itself exempt from consent.',
    duration: '6 months',
  },
  {
    name: '_GRECAPTCHA',
    provider: 'Google',
    purpose: 'Spam and abuse prevention on our contact, demo and trial forms. Loaded only on pages containing a form.',
    duration: '6 months',
  },
];

const ANALYTICS_COOKIES = [
  {
    name: 'sentryReplaySession',
    provider: 'Sentry (Functional Software, Inc.)',
    purpose: 'Session replay used to diagnose errors and usability problems. Text is masked before recording.',
    duration: 'Session (sessionStorage)',
  },
];

const MARKETING_COOKIES = [
  {
    name: '_clck, _clsk',
    provider: 'Microsoft Clarity (first party)',
    purpose: 'Session recording, heatmaps and click tracking.',
    duration: '1 year / session',
  },
  {
    name: 'CLID',
    provider: 'Microsoft (clarity.ms)',
    purpose: 'Identifies your browser across Clarity recordings.',
    duration: '1 year',
  },
  {
    name: 'MUID, ANONCHK, SRM_B',
    provider: 'Microsoft Bing',
    purpose: 'Advertising identifier. Clarity correlates sessions with Bing UET.',
    duration: 'Up to 13 months',
  },
];

function CookieTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm border border-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 font-semibold text-gray-900 border-b border-gray-200">Cookie</th>
            <th className="px-4 py-3 font-semibold text-gray-900 border-b border-gray-200">Provider</th>
            <th className="px-4 py-3 font-semibold text-gray-900 border-b border-gray-200">Purpose</th>
            <th className="px-4 py-3 font-semibold text-gray-900 border-b border-gray-200">Duration</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr key={row.name} className="align-top">
              <td className="px-4 py-3 border-b border-gray-100 font-mono text-xs text-gray-900">{row.name}</td>
              <td className="px-4 py-3 border-b border-gray-100 text-gray-700">{row.provider}</td>
              <td className="px-4 py-3 border-b border-gray-100 text-gray-700">{row.purpose}</td>
              <td className="px-4 py-3 border-b border-gray-100 text-gray-700">{row.duration}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 style={{ color: '#1e638d' }} className="text-xl font-bold mb-4">{title}</h2>
      {children}
    </section>
  );
}

export function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      <div className="py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CompanyBranding onlyLogo={true} />

          <div className="mb-8">
            <h1 style={{ color: '#1e638d' }} className="text-3xl font-bold">SecureOneLabs</h1>
          </div>

          <h1 className="text-4xl font-bold text-gray-900 mb-6">Cookie Policy</h1>

          <p className="text-sm text-[#999999] mb-6">
            Policy version:
            {' '}
            {POLICY_VERSION}
          </p>

          <p style={{ fontSize: '16px', lineHeight: '24px' }} className="text-base text-gray-700 max-w-2xl mx-auto">
            This page lists every cookie and similar technology this site uses, what each one is for,
            and how to change your mind at any time.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Section title="Your current choice">
          <p className="text-gray-700 mb-4">
            Nothing outside the &ldquo;strictly necessary&rdquo; category runs on this site unless you
            have actively agreed to it. Refusing is as easy as accepting, and you can change or
            withdraw your choice at any time.
          </p>

          <button
            type="button"
            style={{ backgroundColor: '#1e638d' }}
            className="px-4 py-2 rounded-lg text-white font-medium"
            onClick={openConsentPreferences}
          >
            Change cookie preferences
          </button>
        </Section>

        <Section title="1. Strictly necessary">
          <p className="text-gray-700 mb-4">
            These are required to deliver the site and cannot be switched off. Under the ePrivacy
            Directive they do not require consent.
          </p>

          <CookieTable rows={NECESSARY_COOKIES} />
        </Section>

        <Section title="2. Analytics — requires your consent">
          <p className="text-gray-700 mb-4">
            Used to understand errors and usability problems. These load only after you accept the
            analytics category.
          </p>

          <CookieTable rows={ANALYTICS_COOKIES} />
        </Section>

        <Section title="3. Marketing — requires your consent">
          <p className="text-gray-700 mb-4">
            Microsoft Clarity is categorised as marketing rather than analytics because it shares an
            advertising identifier with Bing for session correlation. These load only after you
            accept the marketing category.
          </p>

          <CookieTable rows={MARKETING_COOKIES} />
        </Section>

        <Section title="4. Technologies that set no cookie">
          <p className="text-gray-700 mb-2">
            <strong>Cloudflare Web Analytics</strong>
            {' '}
            measures page performance without setting any cookie or storing any
            identifier in your browser, so it runs without consent.
          </p>

          <p className="text-gray-700">
            <strong>Google Maps</strong>
            {' '}
            powers optional address autocomplete on our trial signup form. It is never loaded unless
            you press &ldquo;Enable address autocomplete&rdquo;, and the form can be completed
            without it.
          </p>
        </Section>

        <Section title="5. Record of your consent">
          <p className="text-gray-700 mb-2">
            When you make a choice we store a record of it so we can demonstrate that consent was
            given, as required by Article 7(1) GDPR. That record contains a random identifier, the
            time, the categories you chose, the version of this policy you saw, a truncated IP
            address, and your browser&rsquo;s user agent.
          </p>

          <p className="text-gray-700">
            It does not contain your full IP address, your name, or your email address, and it is not
            linked to any account. We keep these records for 24 months. The legal basis is our
            legitimate interest in demonstrating compliance.
          </p>
        </Section>

        <Section title="6. Changing or withdrawing consent">
          <p className="text-gray-700">
            Use the &ldquo;Change cookie preferences&rdquo; button above, or the
            {' '}
            <strong>Cookie Preferences</strong>
            {' '}
            link in the footer of every page. Withdrawing consent reloads the page so that any
            tracking script already loaded stops running immediately. If this policy changes
            materially we will ask you again.
          </p>
        </Section>
      </div>
    </div>
  );
}
